/**
 * RealtimeConsolePage — 실시간 콘솔 페이지 (/realtime-console)
 *
 * 5상태:
 *   loading  — STOMP CONNECT 진행 중 (핸드셰이크 + 인증 대기) → Skeleton + 상태 Pill "연결 중"
 *   empty    — 구독 직후 메시지 0건 → EmptyState (입력창 활성)
 *   error    — CONNECT 인증 실패 / 핸드셰이크 실패 / WS 비활성 → ErrorState
 *   default  — CONNECT 성공 + 메시지 존재 → 채팅 UI
 *   dialog   — 토픽 변경 inline 드롭다운 (listbox role)
 *
 * WebSocket 전략:
 *   @stomp/stompjs 미포함 → native WebSocket + 직접 STOMP 1.2 프레임 구현
 *   (StompWebSocketClient in api/stompWebSocket.ts)
 *
 * 접근성:
 *   - 메시지 영역: role="log" aria-live="polite" aria-label
 *   - 입력창: aria-label aria-invalid aria-describedby
 *   - 상태 Pill: aria-live="assertive"
 *   - Tab 순서: 토픽 선택 → 입력창 → 전송 버튼
 *   - Esc: 토픽 드롭다운 닫기
 *   - Enter: 전송
 *
 * 반응형:
 *   md (768px) 이상: 풀 레이아웃
 *   이하: 단일 컬럼
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { chatMessageSchema } from '../schema/realtimeConsole.schema';
import type { ChatMessageFormValues } from '../schema/realtimeConsole.schema';
import { useStompClient } from '../hooks/useStompClient';
import { useConsoleHealth } from '../hooks/useConsoleHealth';
import { useActiveSessions } from '../hooks/useActiveSessions';
import { RealtimeConsoleCommonUsagePanel } from '../components/RealtimeConsoleCommonUsagePanel';
import type { ChatMessageView, ConnectionStatus } from '../types/realtimeConsole';

// ─── 타입 ───────────────────────────────────────────────────────────────────

type Room = 'lobby' | 'ops';
const ROOMS: Room[] = ['lobby', 'ops'];

// ─── 유틸리티 ────────────────────────────────────────────────────────────────

function formatTime(isoStr: string): string {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function connectionStatusColor(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'var(--color-success)';
    case 'connecting':
      return 'var(--color-text-secondary)';
    case 'reconnecting':
      return 'var(--color-warning)';
    case 'disconnected':
    case 'authFailed':
      return 'var(--color-danger)';
    default:
      return 'var(--color-text-muted)';
  }
}

function statusPillIcon(status: ConnectionStatus): string {
  switch (status) {
    case 'connected': return '●';
    case 'connecting': return '◐';
    case 'reconnecting': return '◐';
    case 'disconnected': return '○';
    case 'authFailed': return '✕';
    default: return '○';
  }
}

// ─── 서브 컴포넌트 ────────────────────────────────────────────────────────────

/** 연결 상태 Pill */
function ConnectionPill({ status, label }: { status: ConnectionStatus; label: string }) {
  return (
    <span
      role="status"
      aria-live="assertive"
      aria-label={`연결 상태: ${label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        padding: 'var(--spacing-1) var(--spacing-3)',
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${connectionStatusColor(status)}`,
        color: connectionStatusColor(status),
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        background: 'var(--color-surface-elevated)',
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true">{statusPillIcon(status)}</span>
      {label}
    </span>
  );
}

/** 카운터 패널 (접속자 / 누적 메시지 / 엔드포인트 / WS 활성) */
function CounterPanel({
  activeSessions,
  totalMessages,
  endpoint,
  wsActive,
  t,
}: {
  activeSessions: number;
  totalMessages: number;
  endpoint: string;
  wsActive: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div
      role="region"
      aria-label="실시간 콘솔 현황"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--spacing-4)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {t('realtimeConsole.counter.sessions', { count: activeSessions })}
        </span>
        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
          {activeSessions}명
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {t('realtimeConsole.counter.messages', { count: totalMessages })}
        </span>
        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
          {totalMessages.toLocaleString()}건
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {t('realtimeConsole.counter.endpoint')}
        </span>
        <span style={{ fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>
          {endpoint}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {t('realtimeConsole.counter.wsActive')}
        </span>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: wsActive ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {wsActive ? '✓ 활성' : '✗ 비활성'}
        </span>
      </div>
    </div>
  );
}

/** 메시지 행 */
function MessageRow({ msg }: { msg: ChatMessageView }) {
  const isSystem = msg.type === 'SYSTEM';
  return (
    <div
      role="listitem"
      style={{
        display: 'flex',
        alignItems: isSystem ? 'center' : 'flex-start',
        justifyContent: isSystem ? 'center' : 'flex-start',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-2) var(--spacing-3)',
        borderBottom: '1px solid var(--color-surface-border)',
        textAlign: isSystem ? 'center' : 'left',
      }}
    >
      {isSystem ? (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          [SYSTEM] {msg.text}
        </span>
      ) : (
        <>
          <span
            style={{
              fontWeight: 700,
              color: 'var(--color-brand-primary)',
              fontSize: 'var(--font-size-sm)',
              whiteSpace: 'nowrap',
              minWidth: 80,
            }}
          >
            {msg.senderName || msg.sender}:
          </span>
          <span
            style={{
              flex: 1,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              wordBreak: 'break-word',
            }}
          >
            {/* textContent 사용 — XSS 방지 (React 기본 이스케이핑) */}
            {msg.text}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap',
              marginLeft: 'auto',
            }}
          >
            {formatTime(msg.sentAt)}
          </span>
        </>
      )}
    </div>
  );
}

/** 메시지 Skeleton */
function MessageSkeleton() {
  return (
    <div style={{ padding: 'var(--spacing-4)' }} aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            marginBottom: 'var(--spacing-3)',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 72,
              height: 14,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
          <div
            style={{
              flex: 1,
              height: 14,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: `${i * 0.1}s`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── 메인 페이지 ──────────────────────────────────────────────────────────────

/**
 * RealtimeConsolePage
 */
export function RealtimeConsolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 토픽 방 선택 상태
  const [room, setRoom] = useState<Room>('lobby');
  const [roomDropdownOpen, setRoomDropdownOpen] = useState(false);

  // 토스트
  const [toast, setToast] = useState<string | null>(null);

  // STOMP 연결 훅
  const { status, messages, roomCounter, errorCode, publish, reconnect } = useStompClient({
    room,
    enabled: true,
  });

  // REST 보조
  const healthQuery = useConsoleHealth();
  const sessionsQuery = useActiveSessions(status === 'connected');

  // 메시지 영역 auto-scroll ref
  const messageListRef = useRef<HTMLDivElement>(null);

  // 자동 스크롤
  useEffect(() => {
    const el = messageListRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // 폼
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChatMessageFormValues>({
    resolver: zodResolver(chatMessageSchema),
    defaultValues: { text: '', room },
  });

  // room 변경 시 폼 room 값 동기화
  useEffect(() => {
    reset((prev) => ({ ...prev, room }));
  }, [room, reset]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const onSubmit = useCallback(
    (values: ChatMessageFormValues) => {
      if (status !== 'connected') {
        showToast(t('realtimeConsole.error.network'));
        return;
      }
      publish({ room: values.room, text: values.text });
      reset({ text: '', room: values.room });
    },
    [status, publish, reset, showToast, t]
  );

  const handleRoomSelect = useCallback((r: Room) => {
    setRoom(r);
    setRoomDropdownOpen(false);
  }, []);

  const handleRoomKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') setRoomDropdownOpen(false);
    },
    []
  );

  // 상태별 레이블
  const statusLabel = useMemo(() => {
    switch (status) {
      case 'connected': return t('realtimeConsole.status.connected');
      case 'connecting': return t('realtimeConsole.status.connecting');
      case 'reconnecting': return t('realtimeConsole.status.reconnecting');
      case 'disconnected': return t('realtimeConsole.status.disconnected');
      case 'authFailed': return t('realtimeConsole.status.authFailed');
      default: return '';
    }
  }, [status, t]);

  // 카운터 데이터 (STOMP presence 우선, 없으면 REST health)
  const activeSessions = roomCounter?.activeSessions
    ?? healthQuery.data?.totalActiveSessions
    ?? sessionsQuery.data?.length
    ?? 0;
  const totalMessages = roomCounter?.totalMessages ?? 0;
  const wsEndpoint = healthQuery.data?.endpoint ?? '/api/eraf/ws';
  const wsActive = healthQuery.data?.websocketEnabled ?? true;

  // 5상태 판별
  const isConnecting = status === 'connecting';
  const isError = status === 'authFailed' || status === 'disconnected';
  const isConnected = status === 'connected' || status === 'reconnecting';
  const isEmpty = isConnected && messages.length === 0;
  const showMessages = isConnected && messages.length > 0;
  const isInputDisabled = status !== 'connected' || isSubmitting;

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('realtimeConsole.title')}
        breadcrumb={<span>{t('realtimeConsole.title')}</span>}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
            {/* 토픽 선택 드롭다운 (dialog 상태) */}
            <div style={{ position: 'relative' }} onKeyDown={handleRoomKeyDown}>
              <label
                htmlFor="room-toggle"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginRight: 'var(--spacing-1)',
                }}
              >
                {t('realtimeConsole.topic.label')}:
              </label>
              <button
                id="room-toggle"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={roomDropdownOpen}
                aria-label={`토픽 방 선택: 현재 ${room}`}
                onClick={() => setRoomDropdownOpen((v) => !v)}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {room} ▼
              </button>
              {roomDropdownOpen && (
                <ul
                  role="listbox"
                  aria-label={t('realtimeConsole.topic.label')}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 500,
                    background: 'var(--color-surface-background)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-lg)',
                    minWidth: 120,
                    marginTop: 4,
                    padding: 'var(--spacing-1) 0',
                    listStyle: 'none',
                  }}
                >
                  {ROOMS.map((r) => (
                    <li key={r} role="option" aria-selected={r === room}>
                      <button
                        type="button"
                        onClick={() => handleRoomSelect(r)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          background: r === room ? 'var(--color-surface-muted)' : 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          fontSize: 'var(--font-size-sm)',
                          color: r === room ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
                          fontWeight: r === room ? 700 : 400,
                          cursor: 'pointer',
                          fontFamily: 'var(--font-family-sans)',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background =
                            'var(--color-surface-muted)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background =
                            r === room ? 'var(--color-surface-muted)' : 'transparent';
                        }}
                      >
                        {r}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 연결 상태 Pill */}
            <ConnectionPill status={status} label={statusLabel} />
          </div>
        }
      />

      {/* 부제목 */}
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: 'var(--spacing-1) 0 var(--spacing-4)',
        }}
      >
        {t('realtimeConsole.subtitle')}
      </p>

      {/* 카운터 패널 */}
      <CounterPanel
        activeSessions={activeSessions}
        totalMessages={totalMessages}
        endpoint={wsEndpoint}
        wsActive={wsActive}
        t={t}
      />

      {/* 메인 채팅 영역 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 400,
        }}
      >
        {/* loading 상태 — Skeleton */}
        {isConnecting && (
          <div
            aria-label={t('realtimeConsole.status.connecting')}
            aria-busy="true"
            style={{ flex: 1 }}
          >
            <MessageSkeleton />
          </div>
        )}

        {/* error 상태 — ErrorState */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)', flex: 1 }}>
            <ErrorState
              title={
                status === 'authFailed'
                  ? t('realtimeConsole.error.WEBSOCKET-401-001')
                  : t('realtimeConsole.error.network')
              }
              message={
                status === 'authFailed'
                  ? // #633 (R7-087 계열): raw errorCode 단독 노출 대신 안내 문구 + 코드 병기
                    t('realtimeConsole.error.authFailedGuide', {
                      code: errorCode ?? 'WEBSOCKET-401-001',
                    })
                  : t('realtimeConsole.error.network')
              }
              onRetry={status === 'authFailed' ? undefined : reconnect}
            />
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                justifyContent: 'center',
                marginTop: 'var(--spacing-4)',
              }}
            >
              {status !== 'authFailed' && (
                <button
                  type="button"
                  onClick={reconnect}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('realtimeConsole.button.reconnect')}
                </button>
              )}
              {status === 'authFailed' && (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('realtimeConsole.button.relogin')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* WS 비활성 안내 (healthQuery 응답 후 wsActive=false) */}
        {isConnected && !isConnecting && !wsActive && (
          <div style={{ padding: 'var(--spacing-6)', flex: 1 }}>
            <ErrorState
              title={t('realtimeConsole.error.disabled')}
              message="eraf.integration.websocket.enabled=false"
              onRetry={() => void healthQuery.refetch()}
            />
          </div>
        )}

        {/* empty 상태 — EmptyState */}
        {isEmpty && wsActive && (
          <div style={{ padding: 'var(--spacing-6)', flex: 1 }}>
            <EmptyState
              title={t('realtimeConsole.empty.title')}
              description={t('realtimeConsole.empty.description')}
            />
          </div>
        )}

        {/* default 상태 — 메시지 목록 */}
        {showMessages && (
          <div
            ref={messageListRef}
            role="log"
            aria-live="polite"
            aria-label="채팅 메시지 목록"
            aria-relevant="additions"
            style={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: 420,
              minHeight: 200,
            }}
          >
            {messages.map((msg) => (
              <MessageRow key={msg.id} msg={msg} />
            ))}
            <div
              aria-hidden="true"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textAlign: 'right', padding: 'var(--spacing-1) var(--spacing-3)' }}
            >
              ▼ auto-scroll
            </div>
          </div>
        )}

        {/* 재연결 중 배너 */}
        {status === 'reconnecting' && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-warning)',
              color: 'var(--color-text-inverse)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {t('realtimeConsole.toast.reconnecting')}
          </div>
        )}

        {/* 입력창 + 전송 버튼 */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            alignItems: 'flex-start',
          }}
          aria-label="메시지 입력"
        >
          {/* hidden room field */}
          <input type="hidden" {...register('room')} value={room} />

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              type="text"
              id="message-input"
              aria-label={t('realtimeConsole.input.placeholder')}
              aria-invalid={!!errors.text}
              aria-describedby={errors.text ? 'message-error' : undefined}
              placeholder={t('realtimeConsole.input.placeholder')}
              disabled={isInputDisabled}
              autoComplete="off"
              {...register('text')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit(onSubmit)();
                }
              }}
              style={{
                flex: 1,
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${errors.text ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: isInputDisabled ? 'var(--color-surface-muted)' : 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-sans)',
                opacity: isInputDisabled ? 0.6 : 1,
                cursor: isInputDisabled ? 'not-allowed' : 'text',
                outline: 'none',
                width: '100%',
              }}
            />
            {errors.text && (
              <span
                id="message-error"
                role="alert"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                }}
              >
                {errors.text.message}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isInputDisabled}
            aria-label={t('realtimeConsole.input.send')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: isInputDisabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
              color: isInputDisabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isInputDisabled ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isInputDisabled ? 0.6 : 1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {isSubmitting ? '...' : t('realtimeConsole.input.send')}
          </button>
        </form>
      </div>

      {/* 공통 모듈 시연 패널 */}
      <RealtimeConsoleCommonUsagePanel />

      {/* 토픽 드롭다운 외부 클릭 오버레이 */}
      {roomDropdownOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 499 }}
          onClick={() => setRoomDropdownOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 + animation 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          #message-input {
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
}
